import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  LayoutGrid,
  Home,
  TrendingUp,
  Clock,
  ArrowRight,
  Building2,
  DollarSign,
  Bed,
  Bath,
  Square,
  MapPin,
  User,
  ChevronRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ListingCard({ listing }) {
  const statusColors = {
    Active: 'bg-green-100 text-green-700',
    Pending: 'bg-amber-100 text-amber-700',
    Sold: 'bg-blue-100 text-blue-700'
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(price);
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);
  };

  return (
    <Card className="border border-slate-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden" data-testid={`listing-card-${listing.listing_id}`}>
      {listing.property_image ? (
        <div className="h-44 overflow-hidden bg-slate-100">
          <img src={listing.property_image} alt={listing.street_address} className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className="h-44 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
          <Building2 className="w-12 h-12 text-slate-300" />
        </div>
      )}
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-2xl font-bold text-slate-900">{formatPrice(listing.sales_price)}</span>
          <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${statusColors[listing.status] || 'bg-slate-100 text-slate-600'}`}>
            {listing.status}
          </span>
        </div>
        <div className="flex items-start gap-1.5 mb-3">
          <MapPin className="w-3.5 h-3.5 text-teal-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-slate-800">{listing.street_address}</p>
            <p className="text-xs text-slate-500">{listing.city}, {listing.state} {listing.zip_code}</p>
          </div>
        </div>
        <div className="flex items-center gap-4 mb-3 text-sm text-slate-600">
          <span className="flex items-center gap-1"><Bed className="w-3.5 h-3.5" />{listing.bedrooms} bed</span>
          <span className="flex items-center gap-1"><Bath className="w-3.5 h-3.5" />{listing.bathrooms} bath</span>
          <span className="flex items-center gap-1"><Square className="w-3.5 h-3.5" />{listing.square_feet?.toLocaleString()} sqft</span>
        </div>
        <div className="border-t border-slate-100 pt-3 mb-3">
          <p className="text-xs font-medium text-slate-500 mb-2">Commission Splits</p>
          <div className="grid grid-cols-3 gap-2">
            <div className="text-center p-2 rounded-lg bg-blue-50 border border-blue-100">
              <p className="text-[10px] text-slate-500 font-medium">Buyer's Agent</p>
              <p className="text-sm font-bold text-blue-700">{listing.buyer_agent_commission}%</p>
              <p className="text-[10px] text-green-600 font-semibold">{formatAmount(listing.buyer_agent_amount)}</p>
            </div>
            <div className="text-center p-2 rounded-lg bg-purple-50 border border-purple-100">
              <p className="text-[10px] text-slate-500 font-medium">Sub-Agent</p>
              <p className="text-sm font-bold text-purple-700">{listing.sub_agent_commission}%</p>
              <p className="text-[10px] text-green-600 font-semibold">{formatAmount(listing.sub_agent_amount)}</p>
            </div>
            <div className="text-center p-2 rounded-lg bg-amber-50 border border-amber-100">
              <p className="text-[10px] text-slate-500 font-medium">TC</p>
              <p className="text-sm font-bold text-amber-700">{listing.tc_commission}%</p>
              <p className="text-[10px] text-green-600 font-semibold">{formatAmount(listing.tc_amount)}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span className="flex items-center gap-1"><User className="w-3 h-3" />{listing.agent_name}</span>
          <Link to={`/split-report/listings/${listing.listing_id}`} className="text-[#3D4F7A] font-medium hover:underline flex items-center gap-0.5">
            View<ChevronRight className="w-3 h-3" />
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

export { ListingCard };

export default function SplitReportDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total: 0, active: 0, pending: 0, sold: 0, my_listings: 0 });
  const [recentListings, setRecentListings] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(null);

  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    try {
      const res = await fetch(`${API}/split-report/access`, { credentials: 'include' });
      const data = await res.json();
      if (!data.has_access) {
        setHasAccess(false);
        setLoading(false);
        return;
      }
      setHasAccess(true);
      fetchData();
    } catch {
      setHasAccess(false);
      setLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const [statsRes, listingsRes] = await Promise.all([
        fetch(`${API}/split-report/stats`, { credentials: 'include' }),
        fetch(`${API}/split-report/listings?limit=6`, { credentials: 'include' })
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (listingsRes.ok) {
        const data = await listingsRes.json();
        setRecentListings(data.listings || []);
      }
    } catch (e) { console.error('Error:', e); }
    finally { setLoading(false); }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/split-report/listings?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3D4F7A]"></div>
      </div>
    );
  }

  if (hasAccess === false) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Licensed Agents & Brokers Only</h2>
            <p className="text-slate-600 mb-6">Split Report is exclusively for licensed RE Agents, Associate Brokers, and Brokers. Update your role in your profile to gain access.</p>
            <Link to="/dashboard">
              <Button className="bg-[#3D4F7A] hover:bg-[#2A3755]">Back to Dashboard</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statCards = [
    { label: 'Total Listings', value: stats.total, icon: LayoutGrid, color: 'text-blue-600 bg-blue-50' },
    { label: 'Active', value: stats.active, icon: TrendingUp, color: 'text-green-600 bg-green-50' },
    { label: 'Pending', value: stats.pending, icon: Clock, color: 'text-amber-600 bg-amber-50' },
    { label: 'My Listings', value: stats.my_listings, icon: Home, color: 'text-[#3D4F7A] bg-[#3D4F7A]/10' }
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="split-report-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#3D4F7A] to-[#2A3755] py-8">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-6xl">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-white tracking-wide">Split Report</h2>
            <div className="flex items-center gap-2">
              <Link to="/dashboard">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10" data-testid="sr-dashboard-btn">
                  Dashboard
                </Button>
              </Link>
              <Link to="/split-report/new">
                <Button className="bg-teal-500 hover:bg-teal-600 text-white shadow-lg" data-testid="new-listing-btn">
                  <Plus className="w-4 h-4 mr-2" />New Listing
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-6xl py-6">
        {/* Welcome + Search */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-slate-900 mb-1">Welcome back, {user?.name?.split(' ')[0]}!</h2>
          <p className="text-slate-500 mb-4">Here's what's happening in your market today.</p>
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search by address, city, state, or zip code..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3D4F7A]/20 focus:border-[#3D4F7A] bg-white"
                data-testid="search-input"
              />
            </div>
            <Button type="submit" className="bg-[#3D4F7A] hover:bg-[#2A3755] px-6" data-testid="search-btn">Search</Button>
          </form>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {statCards.map((s) => (
            <Card key={s.label} className="border border-slate-200">
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${s.color}`}>
                  <s.icon className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{s.value}</p>
                  <p className="text-xs text-slate-500">{s.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="flex flex-wrap gap-3 mb-8">
          <Link to="/split-report/new">
            <Button className="bg-teal-500 hover:bg-teal-600"><Plus className="w-4 h-4 mr-2" />Add New Listing</Button>
          </Link>
          <Link to="/split-report/listings">
            <Button variant="outline"><LayoutGrid className="w-4 h-4 mr-2" />View All Listings</Button>
          </Link>
          <Link to="/split-report/listings?mine=true">
            <Button variant="outline"><Home className="w-4 h-4 mr-2" />My Listings</Button>
          </Link>
        </div>

        {/* Recent Listings */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-slate-900">Recent Listings</h3>
          <Link to="/split-report/listings" className="text-[#3D4F7A] text-sm font-medium hover:underline flex items-center gap-1">
            View all<ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        {recentListings.length === 0 ? (
          <Card className="border border-slate-200">
            <CardContent className="p-12 text-center">
              <Building2 className="w-16 h-16 text-slate-200 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No listings yet</h3>
              <p className="text-slate-500 mb-4">Be the first to add a listing and share commission split data!</p>
              <Link to="/split-report/new">
                <Button className="bg-teal-500 hover:bg-teal-600"><Plus className="w-4 h-4 mr-2" />Add New Listing</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {recentListings.map((listing) => (
              <ListingCard key={listing.listing_id} listing={listing} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
